/**
 * CRF packet downloads for the CRF Builder page.
 *
 * Two whole-study downloads as plain buttons — every CRF as one printable
 * packet (generateVisitPacketV2 in allForms mode), and the annotated CRF
 * covering every form (generateAnnotatedCRFV2 then downloadAnnotatedCRFV2) —
 * plus the original per-visit packet behind a visit selector.
 *
 * All three end the same way: fetch() the returned signed URL and saveAs() it.
 *
 * (File name kept as VisitPacketExport for continuity; it now covers the
 * whole-study packets too, not just per-visit ones.)
 */
import { useState, useEffect } from 'react';
import { onSnapshot } from 'firebase/firestore';
import { httpsCallable } from '../lib/callable';
import { saveAs } from 'file-saver';
import { Download, Loader2, PackageOpen, PenLine } from 'lucide-react';
import { functions } from '../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../constants/firebase';
import { studyDesignConfigDoc } from '../constants/firestorePaths';
import { acrfErrorMessage } from '../features/dataManagement/acrfErrorMessage';

interface Props {
  projectId: string;
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

interface Visit {
  id: string;
  name: string;
  day?: number;
}

/** Which download is currently running — only one at a time. */
type Busy = 'allForms' | 'acrf' | 'visit' | 'zipWord' | 'zipPdf' | null;

export default function VisitPacketExport({ projectId, showToast }: Props) {
  const [visits, setVisits] = useState<Visit[]>([]);
  const [selectedVisit, setSelectedVisit] = useState('');
  const [busy, setBusy] = useState<Busy>(null);

  useEffect(() => {
    if (!projectId) return;
    const unsub = onSnapshot(
      studyDesignConfigDoc(projectId),
      (snap) => {
        if (snap.exists()) {
          const raw = (snap.data()?.visits as Visit[]) ?? [];
          setVisits(raw.filter((v) => v?.name));
        } else {
          setVisits([]);
        }
      },
      (err) => {
        console.error('[VisitPacketExport] visits load error', err);
        setVisits([]);
      },
    );
    return () => unsub();
  }, [projectId]);

  /** Strip the SDK's wrapper prefixes so the user sees the server's own text. */
  const cleanMessage = (err: unknown): string =>
    ((err as { message?: string })?.message ?? '')
      .replace(/^.*HttpsError[^:]*:\s*/i, '')
      .replace(/^HTTPS callables.*?:\s*/i, '');

  const saveFromUrl = async (downloadUrl: string, filename: string, mimeType?: string) => {
    const resp = await fetch(downloadUrl);
    if (!resp.ok) throw new Error(`Download failed: ${resp.status}`);
    const blob = await resp.blob();
    saveAs(new Blob([blob], mimeType ? { type: mimeType } : undefined), filename);
  };

  // ── Whole study: every CRF as one archive, one file per form ────────
  // Built on the server and delivered by the same short-lived link the packet
  // uses. A form that fails is named rather than silently dropped; the archive
  // still arrives with the rest. The PDF archive renders and SAVES any form
  // with no stored rendering, exactly as "Generate All CRFs" would.
  // Design: docs/plans/2026-09-04-crf-zip-download-design.md.
  const downloadAllAsZip = async (format: 'word' | 'pdf') => {
    if (busy) return;
    setBusy(format === 'word' ? 'zipWord' : 'zipPdf');
    try {
      const fn = httpsCallable<
        { projectId: string; format: 'word' | 'pdf' },
        { downloadUrl: string; filename: string; mimeType?: string; included: number; omitted: Array<{ name: string; reason: string }> }
      >(functions, CLOUD_FUNCTIONS.EXPORT_ALL_CRFS_AS_ZIP, { timeout: CALLABLE_TIMEOUTS.LONG });
      const { data } = await fn({ projectId, format });
      await saveFromUrl(data.downloadUrl, data.filename, data.mimeType || 'application/zip');
      showToast(`Downloaded ${data.included} ${format === 'word' ? 'Word' : 'PDF'} CRF${data.included === 1 ? '' : 's'} as ${data.filename}`, 'success');
      if (data.omitted?.length) {
        showToast(`Not included: ${data.omitted.map((o) => o.name).join(', ')}. Use Generate CRF on those forms.`, 'error');
      }
    } catch (err) {
      showToast(`Download all as ${format === 'word' ? 'Word' : 'PDF'} failed: ${cleanMessage(err) || 'unknown error'}`, 'error');
    } finally {
      setBusy(null);
    }
  };

  // ── Whole study: every CRF in one printable packet ──────────────────
  const downloadAllFormsPacket = async () => {
    if (busy) return;
    setBusy('allForms');
    try {
      const fn = httpsCallable<
        { projectId: string; allForms: boolean },
        { downloadUrl: string; filename: string; mimeType: string; verification?: { status?: string } }
      >(functions, CLOUD_FUNCTIONS.GENERATE_VISIT_PACKET, { timeout: CALLABLE_TIMEOUTS.LONG });
      const { data } = await fn({ projectId, allForms: true });
      await saveFromUrl(data.downloadUrl, data.filename, data.mimeType);
      // Inc5 step G: composites are available-with-warning — say so honestly.
      if (data.verification?.status && data.verification.status !== 'clean') {
        showToast(
          'The packet downloaded, but its verification found differences — see the Verification tab.',
          'info',
        );
      }
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code ?? '';
      const msg = cleanMessage(err);
      if (code === 'functions/failed-precondition') {
        showToast(msg || 'This study has no CRF forms yet.', 'error');
      } else {
        console.error('[VisitPacketExport] all-forms packet error', err);
        showToast(msg || 'Failed to build the all-forms packet. Please try again.', 'error');
      }
    } finally {
      setBusy(null);
    }
  };

  // ── Whole study: annotated CRF ──────────────────────────────────────
  // Generate then download: the generator writes the PDF to
  // dataManagement/acrf and the downloader returns a signed URL for it. We
  // always regenerate so the file reflects the study's current forms rather
  // than whatever was stored on some earlier run.
  const downloadAnnotatedCrf = async () => {
    if (busy) return;
    setBusy('acrf');
    try {
      const generate = httpsCallable<{ projectId: string }, unknown>(
        functions,
        CLOUD_FUNCTIONS.GENERATE_ANNOTATED_CRF,
        { timeout: CALLABLE_TIMEOUTS.LONG },
      );
      await generate({ projectId });

      const download = httpsCallable<
        { projectId: string },
        { downloadUrl: string; filename: string; mimeType?: string }
      >(functions, CLOUD_FUNCTIONS.DOWNLOAD_ANNOTATED_CRF, { timeout: CALLABLE_TIMEOUTS.MEDIUM });
      const { data } = await download({ projectId });
      await saveFromUrl(data.downloadUrl, data.filename || 'annotated_CRF.pdf', data.mimeType);
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code ?? '';
      if (code === 'functions/not-found') {
        showToast(cleanMessage(err) || 'Add CRF forms before building an annotated CRF.', 'error');
      } else {
        if (code !== 'functions/failed-precondition') console.error('[VisitPacketExport] aCRF error', err);
        showToast(acrfErrorMessage(err as { code?: string; message?: string }), 'error');
      }
    } finally {
      setBusy(null);
    }
  };

  // ── Single visit ────────────────────────────────────────────────────
  const downloadVisitPacket = async () => {
    if (!selectedVisit || busy) return;
    setBusy('visit');
    try {
      const fn = httpsCallable<
        { projectId: string; visitName: string },
        { downloadUrl: string; filename: string; mimeType: string; verification?: { status?: string } }
      >(functions, CLOUD_FUNCTIONS.GENERATE_VISIT_PACKET, { timeout: CALLABLE_TIMEOUTS.LONG });
      const { data } = await fn({ projectId, visitName: selectedVisit });
      await saveFromUrl(data.downloadUrl, data.filename, data.mimeType);
      // Inc5 step G: composites are available-with-warning — say so honestly.
      if (data.verification?.status && data.verification.status !== 'clean') {
        showToast(
          'The packet downloaded, but its verification found differences — see the Verification tab.',
          'info',
        );
      }
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code ?? '';
      const msg = cleanMessage(err);
      if (code === 'functions/not-found') {
        showToast(
          `No CRF forms are assigned to "${selectedVisit}". Use the Form Assignments view to assign forms first, then save.`,
          'error',
        );
      } else if (code === 'functions/failed-precondition') {
        showToast(msg || `No forms are assigned to "${selectedVisit}".`, 'error');
      } else {
        console.error('[VisitPacketExport] visit packet error', err);
        showToast(msg || 'Failed to generate the visit packet. Please try again.', 'error');
      }
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-6 p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
      <div className="flex items-center gap-2 mb-1">
        <PackageOpen className="h-4 w-4 text-indigo-600" />
        <h3 className="text-sm font-semibold text-gray-800">CRF Packets</h3>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Printable PDFs with shared patient header fields that auto-fill across every page.
      </p>

      {/* Whole study — the two full packets, always available */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={downloadAllFormsPacket}
          disabled={busy !== null}
          title="Every CRF in this study as one printable packet"
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {busy === 'allForms' ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          {busy === 'allForms' ? 'Building…' : 'Download All Forms Packet'}
        </button>
        <button
          onClick={downloadAnnotatedCrf}
          disabled={busy !== null}
          title="Every CRF annotated with its SDTM domain and variable names, for submission"
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {busy === 'acrf' ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <PenLine className="h-4 w-4" />
          )}
          {busy === 'acrf' ? 'Building…' : 'Download Annotated CRF'}
        </button>
      </div>

      {/* Whole study — every form as its own file, in one archive */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <p className="text-xs text-gray-500 mb-2">
          Or every form as a separate file, in one zip.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => downloadAllAsZip('word')}
            disabled={busy !== null}
            title="Every form as an editable Word document, one file each, in one zip"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {busy === 'zipWord' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {busy === 'zipWord' ? 'Building…' : 'Download All as Word (.zip)'}
          </button>
          <button
            onClick={() => downloadAllAsZip('pdf')}
            disabled={busy !== null}
            title="Every form as a PDF, one file each, in one zip. A form with no stored rendering is rendered and saved first."
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {busy === 'zipPdf' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {busy === 'zipPdf' ? 'Building…' : 'Download All as PDF (.zip)'}
          </button>
        </div>
      </div>

      {/* Single visit — only meaningful once visits exist */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        {visits.length === 0 ? (
          <p className="text-xs text-gray-500">
            Add visits in Study Design to download a packet for a single visit.
          </p>
        ) : (
          <>
            <p className="text-xs text-gray-500 mb-2">
              Or download just the forms assigned to one visit. Requires forms to be assigned and
              saved in the Form Assignments view.
            </p>
            <div className="flex items-center gap-3">
              <select
                value={selectedVisit}
                onChange={(e) => setSelectedVisit(e.target.value)}
                disabled={busy !== null}
                className="flex-1 text-sm border border-gray-300 rounded-md px-3 py-2 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50"
              >
                <option value="">Select a visit…</option>
                {visits.map((v) => (
                  <option key={v.id ?? v.name} value={v.name}>
                    {v.name}
                  </option>
                ))}
              </select>
              <button
                onClick={downloadVisitPacket}
                disabled={!selectedVisit || busy !== null}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {busy === 'visit' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {busy === 'visit' ? 'Generating…' : 'Download Visit Packet'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
